import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Settings, 
  Phone, 
  FileText, 
  Webhook, 
  Inbox, 
  AlertTriangle, 
  Shield,
  Play,
  Pause,
  RotateCcw,
  Zap,
  RefreshCw,
  Eye,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  BarChart3,
  Download
} from 'lucide-react';
import { Button } from '../../ui/button';
import { nativeCommsAPI } from '../../../api/nativeComms';
import { 
  CommsHealth, 
  Provider, 
  NumberInv, 
  Template, 
  Event, 
  MessageRow,
  ProviderStatus 
} from '../../../types/nativeComms';
import toast from 'react-hot-toast';

const StatusBadge = ({ status, children }: { status: ProviderStatus; children: React.ReactNode }) => {
  const colors = {
    healthy: 'bg-green-500/20 border-green-500/30 text-green-400',
    degraded: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
    down: 'bg-red-500/20 border-red-500/30 text-red-400'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs border ${colors[status]}`}>
      {children}
    </span>
  );
};

const MessageStatusBadge = ({ status }: { status: string }) => {
  const colors = {
    QUEUED: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
    SENT: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400',
    DELIVERED: 'bg-green-500/20 border-green-500/30 text-green-400',
    FAILED: 'bg-red-500/20 border-red-500/30 text-red-400',
    DEFERRED: 'bg-orange-500/20 border-orange-500/30 text-orange-400',
    THROTTLED: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
    OPTOUT: 'bg-gray-500/20 border-gray-500/30 text-gray-400'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs border ${colors[status as keyof typeof colors] || colors.QUEUED}`}>
      {status}
    </span>
  );
};

export const NativeCommsTab: React.FC = () => {
  const [health, setHealth] = useState<CommsHealth | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [numbers, setNumbers] = useState<NumberInv[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [testSendForm, setTestSendForm] = useState({ to: '', vars: {} as Record<string, string> });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [healthData, providersData, numbersData, templatesData, eventsData, messagesData] = await Promise.all([
        nativeCommsAPI.getHealth(),
        nativeCommsAPI.getProviders(),
        nativeCommsAPI.getNumbers(),
        nativeCommsAPI.getTemplates(),
        nativeCommsAPI.getEvents(),
        nativeCommsAPI.getMessages()
      ]);
      
      setHealth(healthData);
      setProviders(providersData);
      setNumbers(numbersData);
      setTemplates(templatesData);
      setEvents(eventsData);
      setMessages(messagesData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminAction = async (action: () => Promise<void>, actionName: string) => {
    try {
      await action();
      toast.success(`${actionName} completed successfully`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error(`Failed to ${actionName.toLowerCase()}:`, error);
      toast.error(`Failed to ${actionName.toLowerCase()}`);
    }
  };

  const handleTestSend = async () => {
    if (!selectedTemplate || !testSendForm.to) {
      toast.error('Please select a template and enter recipient');
      return;
    }

    try {
      await nativeCommsAPI.testSend({
        templateId: selectedTemplate.id,
        to: testSendForm.to,
        vars: testSendForm.vars
      });
      toast.success('Test message sent successfully');
      setTestSendForm({ to: '', vars: {} });
    } catch (error) {
      console.error('Failed to send test message:', error);
      toast.error('Failed to send test message');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-64 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pipeline Health Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Activity size={24} className="text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Pipeline Health Controls</h3>
          </div>
          <div className="flex space-x-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleAdminAction(nativeCommsAPI.pauseRouting, 'Pause Routing')}
              className="text-xs"
            >
              <Pause className="w-3 h-3 mr-1" />
              Pause
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleAdminAction(nativeCommsAPI.resumeRouting, 'Resume Routing')}
              className="text-xs"
            >
              <Play className="w-3 h-3 mr-1" />
              Resume
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleAdminAction(nativeCommsAPI.drainQueue, 'Drain Queue')}
              className="text-xs"
            >
              <RotateCcw className="w-3 h-3 mr-1" />
              Drain
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleAdminAction(nativeCommsAPI.failoverToBackup, 'Failover to Backup')}
              className="text-xs"
            >
              <Zap className="w-3 h-3 mr-1" />
              Failover
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => handleAdminAction(nativeCommsAPI.clearThrottleWindow, 'Clear Throttle')}
              className="text-xs"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Clear Throttle
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Delivered % */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Delivered %</h3>
            <CheckCircle size={20} className="text-green-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.deliveredPct || 0}%
          </div>
          <p className="text-sm text-slate-400">Success Rate</p>
        </motion.div>

        {/* Send Success % */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Send Success %</h3>
            <Send size={20} className="text-blue-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.sendSuccessPct || 0}%
          </div>
          <p className="text-sm text-slate-400">API Success</p>
        </motion.div>

        {/* DLR Receipt Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">DLR Receipt Rate</h3>
            <TrendingUp size={20} className="text-cyan-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.dlrRatePct || 0}%
          </div>
          <p className="text-sm text-slate-400">Delivery Receipts</p>
        </motion.div>

        {/* DLR Latency */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">DLR Latency (p50/p95)</h3>
            <Clock size={20} className="text-blue-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.dlrLatencyMs?.p50 || 0}ms / {health?.dlrLatencyMs?.p95 || 0}ms
          </div>
          <p className="text-sm text-slate-400">Response Time</p>
        </motion.div>

        {/* Bounce % */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Bounce %</h3>
            <XCircle size={20} className="text-orange-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.bouncePct || 0}%
          </div>
          <p className="text-sm text-slate-400">Failed Delivery</p>
        </motion.div>

        {/* Opt-out % */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Opt-out %</h3>
            <Shield size={20} className="text-red-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.optOutPct || 0}%
          </div>
          <p className="text-sm text-slate-400">Unsubscribe Rate</p>
        </motion.div>

        {/* Queue Depth */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Queue Depth</h3>
            <Inbox size={20} className="text-yellow-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.queue.depth || 0}
          </div>
          <p className="text-sm text-slate-400">Pending Messages</p>
        </motion.div>

        {/* Retry Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Retry Rate</h3>
            <RotateCcw size={20} className="text-orange-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {health?.queue.retryRatePct || 0}%
          </div>
          <p className="text-sm text-slate-400">Retry Attempts</p>
        </motion.div>

        {/* Cost/Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Cost/Message</h3>
            <BarChart3 size={20} className="text-purple-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            ${health?.costPerMessageUsd || 0}
          </div>
          <p className="text-sm text-slate-400">24h Rolling Average</p>
        </motion.div>
      </div>

      {/* Provider Routing */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Settings size={24} className="text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Provider Status & Performance</h3>
          </div>
          <Button size="sm" variant="outline" className="text-xs">
            <Settings className="w-3 h-3 mr-1" />
            Configure
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-2 text-slate-300 font-semibold">Provider</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Status</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Region</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Max RPS</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Current RPS</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Error Rate</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Cost Weight</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Priority</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((provider) => (
                <tr key={provider.id} className="border-b border-slate-800">
                  <td className="py-2 text-white">{provider.name}</td>
                  <td className="py-2">
                    <StatusBadge status={provider.status}>
                      {provider.status}
                    </StatusBadge>
                  </td>
                  <td className="py-2 text-slate-300">{provider.region}</td>
                  <td className="py-2 text-slate-300">{provider.maxRps}</td>
                  <td className="py-2 text-slate-300">{provider.currentRps}</td>
                  <td className="py-2 text-slate-300">{provider.errRatePct}%</td>
                  <td className="py-2 text-slate-300">{provider.costWeight}</td>
                  <td className="py-2 text-slate-300">{provider.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Numbers & Sender IDs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Phone size={24} className="text-green-400" />
            <h3 className="text-lg font-semibold text-white">Phone Numbers & Sender IDs</h3>
          </div>
          <Button size="sm" variant="outline" className="text-xs">
            <Phone className="w-3 h-3 mr-1" />
            Manage
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-2 text-slate-300 font-semibold">Number/ID</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Channel</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Verification</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Tenant</th>
                <th className="text-left py-2 text-slate-300 font-semibold">RPS Cap</th>
              </tr>
            </thead>
            <tbody>
              {numbers.map((number) => (
                <tr key={number.id} className="border-b border-slate-800">
                  <td className="py-2 text-white">{number.e164}</td>
                  <td className="py-2 text-slate-300">{number.channel}</td>
                  <td className="py-2">
                    {number.verified ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                  </td>
                  <td className="py-2 text-slate-300">{number.tenant || '-'}</td>
                  <td className="py-2 text-slate-300">{number.rpsCap}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Templates & Variables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates Library */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 }}
          className="evolut-metric-card p-6 lg:col-span-2"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <FileText size={24} className="text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Template Library</h3>
            </div>
            <Button size="sm" variant="outline" className="text-xs">
              <FileText className="w-3 h-3 mr-1" />
              Create
            </Button>
          </div>
          <div className="space-y-3">
            {templates.map((template) => (
              <div 
                key={template.id} 
                className={`p-3 rounded border cursor-pointer transition-colors ${
                  selectedTemplate?.id === template.id 
                    ? 'border-blue-500 bg-blue-500/10' 
                    : 'border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => setSelectedTemplate(template)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white">{template.name}</div>
                    <div className="text-xs text-slate-400">
                      {template.channel} • v{template.version} • {template.vars.length} variables
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">
                    {new Date(template.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Template Preview & Test */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center space-x-3 mb-6">
            <Eye size={24} className="text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Preview & Test</h3>
          </div>
          {selectedTemplate ? (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">Channel: {selectedTemplate.channel}</div>
              <div className="text-sm bg-slate-800 p-2 rounded text-white">
                {selectedTemplate.body}
              </div>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Recipient"
                  value={testSendForm.to}
                  onChange={(e) => setTestSendForm({ ...testSendForm, to: e.target.value })}
                  className="w-full px-2 py-1 text-sm bg-slate-800 border border-slate-600 rounded text-white placeholder-slate-400"
                />
                <Button size="sm" onClick={handleTestSend} className="w-full">
                  <Send className="w-3 h-3 mr-1" />
                  Test Send
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400">Select a template to preview</div>
          )}
        </motion.div>
      </div>

      {/* Webhooks & Events */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Webhook size={24} className="text-orange-400" />
            <h3 className="text-lg font-semibold text-white">Live Events Stream</h3>
          </div>
          <Button size="sm" variant="outline" className="text-xs">
            <Webhook className="w-3 h-3 mr-1" />
            Configure
          </Button>
        </div>
        <div className="space-y-2 max-h-32 overflow-y-auto">
          {events.map((event) => (
            <div key={event.id} className="flex items-center justify-between py-2 border-b border-slate-800 last:border-b-0">
              <div className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full ${
                  event.kind === 'DLR' ? 'bg-green-400' : 
                  event.kind === 'MO' ? 'bg-blue-400' : 'bg-purple-400'
                }`}></div>
                <div>
                  <div className="text-sm text-white">{event.kind} - {event.status}</div>
                  <div className="text-xs text-slate-400">{event.providerId}</div>
                </div>
              </div>
              <span className="text-xs text-slate-400">
                {new Date(event.ts).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Message Status & History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Inbox size={24} className="text-indigo-400" />
            <h3 className="text-lg font-semibold text-white">Message Status & History</h3>
          </div>
          <div className="flex space-x-2">
            <Button size="sm" variant="outline" className="text-xs">
              <Eye className="w-3 h-3 mr-1" />
              View All
            </Button>
            <Button size="sm" variant="outline" className="text-xs">
              <Download className="w-3 h-3 mr-1" />
              Export
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left py-2 text-slate-300 font-semibold">ID</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Tenant</th>
                <th className="text-left py-2 text-slate-300 font-semibold">To</th>
                <th className="text-left py-2 text-slate-300 font-semibold">From</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Template</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Status</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Attempts</th>
                <th className="text-left py-2 text-slate-300 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <tr key={message.id} className="border-b border-slate-800">
                  <td className="py-2 text-xs text-slate-400">{message.id}</td>
                  <td className="py-2 text-white">{message.tenant}</td>
                  <td className="py-2 text-slate-300">{message.to}</td>
                  <td className="py-2 text-slate-300">{message.from || '-'}</td>
                  <td className="py-2 text-slate-300">{message.templateId || '-'}</td>
                  <td className="py-2">
                    <MessageStatusBadge status={message.status} />
                  </td>
                  <td className="py-2 text-slate-300">{message.attempts}</td>
                  <td className="py-2 text-xs text-slate-400">
                    {new Date(message.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Dead Letter Queue */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle size={24} className="text-red-400" />
            <h3 className="text-lg font-semibold text-white">Dead Letter Queue Statistics</h3>
          </div>
          <Button size="sm" variant="outline" className="text-xs">
            <RotateCcw className="w-3 h-3 mr-1" />
            Replay
          </Button>
        </div>
        <div className="grid grid-cols-5 gap-4">
          {health?.errorsByClass.map((errorClass) => (
            <div key={errorClass.class} className="text-center">
              <div className="text-lg font-semibold text-red-400">{errorClass.count}</div>
              <div className="text-xs text-slate-400 capitalize">
                {errorClass.class.replace('_', ' ')}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Compliance & Opt-outs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        className="evolut-metric-card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Shield size={24} className="text-green-400" />
            <h3 className="text-lg font-semibold text-white">Compliance Status & Rules</h3>
          </div>
          <Button size="sm" variant="outline" className="text-xs">
            <Shield className="w-3 h-3 mr-1" />
            Settings
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-lg font-semibold text-green-400">{health?.optOutPct || 0}%</div>
            <div className="text-xs text-slate-400">Opt-out Rate</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-blue-400">24/7</div>
            <div className="text-xs text-slate-400">Quiet Hours</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-purple-400">Active</div>
            <div className="text-xs text-slate-400">TCPA Compliance</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
